/**
 * Improve.jsx — Pocket FM-style per-title analytics for scribes / admins /
 * co-admins (read-only). Mounted at `/improve` and deep-linked from
 * WriterJourney + ManageContent rows via `?ref_id=<id>`.
 *
 * Layout (mirrors the IMG_6642 mockup):
 *   • Content picker (drop-down of every series + ebook the caller owns)
 *   • Window pills: Last 7 days · 30 days · All time
 *   • KPI grid: Active Listeners · Comments · Total Revenue Share · Avg play
 *   • Tabs: Analytics (thresholds + tiny breakdown)  /  Comments (threaded)
 *
 * Data lives entirely behind /api/improve/*. Writers see only their own
 * titles; admins/co-admins see every title.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, ChevronDown, Info, MessageCircle, Loader2, Users, Clock,
  BarChart3, Sparkles, Bookmark, CheckCircle2, Headphones, Timer,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { authFetch, checkAuth } from '../utils/authUtils';
import { resolveCoverUrl } from '../utils/coverUrl';
import ImproveCommentRow from '../components/ImproveCommentRow';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const WINDOWS = [
  { id: '7d', label: 'Last 7 days' },
  { id: '30d', label: '30 days' },
  { id: 'all', label: 'All time' },
];

const fmtCount = (n) => {
  if (n == null) return '0';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return `${n}`;
};

const fmtDuration = (sec) => {
  if (!sec) return '0 m';
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (h && m) return `${h} h ${m} m`;
  if (h) return `${h} h`;
  return `${m} m`;
};

const fmtRel = (iso) => {
  if (!iso) return '';
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
};

const ACCENT_RING = {
  indigo: 'hover:border-indigo-500/40',
  emerald: 'hover:border-emerald-500/40',
  amber: 'hover:border-amber-500/40',
  cyan: 'hover:border-cyan-500/40',
};
const ACCENT_ICON = {
  indigo: 'text-indigo-300',
  emerald: 'text-emerald-300',
  amber: 'text-amber-300',
  cyan: 'text-cyan-300',
};

// ─── Metric registry — drives the toggle pills + the chart legend. ───
// Each entry tells the chart how to label the axis, what colour to draw
// the line with (defines its own SVG <linearGradient>), and how to format
// a single value for tooltips/peak callouts.
const METRICS = [
  {
    id: 'dal',
    label: 'Listeners',
    Icon: Users,
    stroke: '#a78bfa',          // violet-400
    fillId: 'metric-dal-fill',
    fillStops: [
      { offset: '0%',   color: '#a78bfa', opacity: 0.4 },
      { offset: '100%', color: '#a78bfa', opacity: 0   },
    ],
    fmt: (v) => `${v}`,
    legend: 'Each point = unique listeners on that day.',
  },
  {
    id: 'minutes',
    label: 'Minutes',
    Icon: Timer,
    stroke: '#34d399',
    fillId: 'metric-minutes-fill',
    fillStops: [
      { offset: '0%',   color: '#34d399', opacity: 0.35 },
      { offset: '100%', color: '#34d399', opacity: 0    },
    ],
    fmt: (v) => `${v} m`,
    legend: 'Total minutes spent listening on that day.',
  },
  {
    id: 'completions',
    label: 'Completions',
    Icon: CheckCircle2,
    stroke: '#f59e0b',
    fillId: 'metric-completions-fill',
    fillStops: [
      { offset: '0%',   color: '#f59e0b', opacity: 0.35 },
      { offset: '100%', color: '#f59e0b', opacity: 0    },
    ],
    fmt: (v) => `${v}`,
    legend: 'Episodes / books marked completed on that day.',
  },
  {
    id: 'saves',
    label: 'Saves',
    Icon: Bookmark,
    stroke: '#22d3ee',
    fillId: 'metric-saves-fill',
    fillStops: [
      { offset: '0%',   color: '#22d3ee', opacity: 0.35 },
      { offset: '100%', color: '#22d3ee', opacity: 0    },
    ],
    fmt: (v) => `${v}`,
    legend: 'Users who saved this title to their library that day.',
  },
];

const findMetric = (id) => METRICS.find((m) => m.id === id) || METRICS[0];

// ─── Metric chart ─────────────────────────────────────────────────────
// Generic timeseries chart driven by the METRICS registry. Consumes the
// `[{date, value}, …]` shape that /api/improve/{ref_id}/timeseries returns
// for ALL metric ids, so adding a new metric is just an entry in METRICS
// + a backend branch — no chart code changes needed.
const MetricChart = ({ data, window, metric }) => {
  const series = Array.isArray(data) ? data : [];
  const W = 720, H = 200, P = 28;
  const max = Math.max(1, ...series.map((d) => Number(d.value) || 0));
  const labelEvery = series.length > 14 ? Math.ceil(series.length / 7) : 1;
  const points = series.length === 0
    ? ''
    : series.map((d, i) => {
        const x = P + (series.length === 1 ? (W - 2 * P) / 2 : (i / (series.length - 1)) * (W - 2 * P));
        const y = H - P - ((Number(d.value) || 0) / max) * (H - 2 * P);
        return `${x.toFixed(1)},${y.toFixed(1)}`;
      }).join(' ');
  const peak = series.reduce(
    (acc, d) => ((Number(d.value) || 0) > (Number(acc?.value) || 0) ? d : acc),
    null,
  );
  const total = series.reduce((s, d) => s + (Number(d.value) || 0), 0);

  return (
    <div className="rounded-2xl bg-slate-900/40 border border-slate-800 p-4" data-testid="improve-metric-chart">
      <header className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <h3 className="text-sm font-bold text-white inline-flex items-center gap-2">
          {metric.Icon && <metric.Icon className="w-4 h-4" style={{ color: metric.stroke }} />}
          {metric.label}
        </h3>
        <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">
          {window === '7d' ? 'Last 7 days' : window === '30d' ? 'Last 30 days' : 'All time'}
          {total > 0 && (
            <span className="ml-2 text-white">· total <span className="tabular-nums font-extrabold">{metric.fmt(total)}</span></span>
          )}
        </p>
      </header>
      {series.length === 0 ? (
        <p className="text-sm text-slate-500 italic py-8 text-center">
          No {metric.label.toLowerCase()} data yet for this window. Once readers tune in, the chart will populate.
        </p>
      ) : (
        <>
          <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-44">
            <defs>
              <linearGradient id={metric.fillId} x1="0" y1="0" x2="0" y2="1">
                {metric.fillStops.map((s) => (
                  <stop key={s.offset} offset={s.offset} stopColor={s.color} stopOpacity={s.opacity} />
                ))}
              </linearGradient>
            </defs>
            {[0.25, 0.5, 0.75, 1].map((g) => (
              <line key={g} x1={P} x2={W - P} y1={P + g * (H - 2 * P)} y2={P + g * (H - 2 * P)} stroke="#1e293b" strokeDasharray="2 4" />
            ))}
            {series.length >= 2 && (
              <polygon
                fill={`url(#${metric.fillId})`}
                points={`${P},${H - P} ${points} ${W - P},${H - P}`}
              />
            )}
            <polyline fill="none" stroke={metric.stroke} strokeWidth="2.2" points={points} />
            {series.map((d, i) => {
              const x = P + (series.length === 1 ? (W - 2 * P) / 2 : (i / (series.length - 1)) * (W - 2 * P));
              const y = H - P - ((Number(d.value) || 0) / max) * (H - 2 * P);
              return <circle key={d.date} cx={x} cy={y} r="3" fill={metric.stroke}><title>{`${d.date} · ${metric.fmt(d.value)}`}</title></circle>;
            })}
            {series.map((d, i) => {
              if (i % labelEvery !== 0 && i !== series.length - 1) return null;
              const x = P + (series.length === 1 ? (W - 2 * P) / 2 : (i / (series.length - 1)) * (W - 2 * P));
              return (
                <text key={`l-${d.date}`} x={x} y={H - 6} fontSize="10" fill="#64748b" textAnchor="middle">
                  {d.date.slice(5)}
                </text>
              );
            })}
            <text x={P} y={P - 8} fontSize="10" fill="#64748b">{metric.fmt(max)}</text>
          </svg>
          <div className="flex items-center justify-between text-[11px] mt-1">
            <p className="text-slate-500">{metric.legend}</p>
            {peak && (
              <p className="text-slate-300">
                Peak <span className="font-bold tabular-nums" style={{ color: metric.stroke }}>{metric.fmt(peak.value)}</span> on {peak.date}
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

// (DailyActiveListenersChart was a thin alias around MetricChart — removed
// after the metric registry made it redundant.)

// ─── Engagement gate ──────────────────────────────────────────────────
const EngagementPane = ({ episodeCount = 0, unlockAt = 50 }) => {
  const unlocked = episodeCount >= unlockAt;
  const pct = Math.min(100, Math.round((episodeCount / unlockAt) * 100));
  return (
    <div className="rounded-2xl bg-slate-900/40 border border-slate-800 p-5 text-center" data-testid="improve-engagement-pane">
      <Sparkles className="w-7 h-7 text-fuchsia-300 mx-auto mb-2" />
      <h3 className="text-base font-bold text-white">
        {unlocked ? 'Engagement insights' : `Engagement insights unlock at Episode ${unlockAt}`}
      </h3>
      <p className="text-xs text-slate-400 mt-1 max-w-md mx-auto">
        {unlocked
          ? 'Per-episode retention, drop-off heat-map and binge-watch ratios are live below. Refresh after each new upload to see fresh data.'
          : `Once your series crosses ${unlockAt} episodes, we'll show the per-episode retention curve, drop-off heat-map and binge-watch ratios. We need a long-enough tail to make the numbers actually mean something.`}
      </p>
      {!unlocked && (
        <div className="mt-4 mx-auto max-w-xs">
          <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-fuchsia-500 to-indigo-500 transition-all"
              style={{ width: `${pct}%` }}
              data-testid="engagement-progress-bar"
            />
          </div>
          <p className="text-[11px] text-slate-500 mt-1.5 tabular-nums">
            {episodeCount} / {unlockAt} episodes · {unlockAt - episodeCount} more to unlock
          </p>
        </div>
      )}
      {unlocked && (
        <p className="text-[11px] text-amber-300 mt-3 italic">
          🚧 Retention curve rendering is rolling out — your data is being processed.
        </p>
      )}
    </div>
  );
};

const Tile = ({ icon: Icon, label, value, hint, accent = 'indigo', tooltip, testId }) => (  <div
    className={`rounded-2xl bg-slate-900/60 border border-slate-800 p-4 sm:p-5 flex flex-col gap-2 transition ${ACCENT_RING[accent] || ''}`}
    data-testid={testId}
  >
    <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-400 font-bold">
      {Icon && <Icon className={`w-3.5 h-3.5 ${ACCENT_ICON[accent] || 'text-indigo-300'}`} />}
      <span>{label}</span>
      {tooltip && (
        <span title={tooltip} className="cursor-help">
          <Info className="w-3 h-3 text-slate-500" />
        </span>
      )}
    </div>
    <div className="text-3xl sm:text-4xl font-black text-white tabular-nums leading-none">
      {value}
    </div>
    {hint && <div className="text-[11px] text-slate-500">{hint}</div>}
  </div>
);

const CommentRow = ImproveCommentRow;

const Improve = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [me, setMe] = useState(null);
  const [items, setItems] = useState([]);
  const [refId, setRefId] = useState(params.get('ref_id') || '');
  const [timeWindow, setTimeWindow] = useState('7d');
  const [summary, setSummary] = useState(null);
  const [tab, setTab] = useState('analytics');
  const [threadData, setThread] = useState({ thread: [], total: 0, episodes: [] });
  const [filterEpisode, setFilterEpisode] = useState('');
  const [sort, setSort] = useState('relevant');
  const [loading, setLoading] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [busyReplyId, setBusyReplyId] = useState(null);
  // Pocket-FM-style sub-tabs inside the Analytics tab: Overview (Daily Active
  // Listeners chart) vs Engagement (episode-retention curve, gated until
  // the series has ≥50 episodes).
  const [analyticsView, setAnalyticsView] = useState('overview');
  const [overviewMetric, setOverviewMetric] = useState('dal');
  const [timeseries, setTimeseries] = useState({ series: [], episode_count: 0 });

  // Gate: writers/admins/co-admins only.
  useEffect(() => {
    (async () => {
      const u = await checkAuth();
      if (!u) { navigate('/login'); return; }
      if (!['writer', 'admin', 'coadmin'].includes(u.role)) {
        toast.error('Improve is for scribes & admins only');
        navigate(-1);
        return;
      }
      setMe(u);
    })();
  }, [navigate]);

  // Fetch the picker list.
  useEffect(() => {
    if (!me) return;
    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/api/improve/my-content`);
        if (r.ok) {
          const d = await r.json();
          setItems(d.items || []);
          if (!refId && d.items?.length) {
            const first = d.items[0].ref_id;
            setRefId(first);
            setParams({ ref_id: first }, { replace: true });
          }
        }
      } catch (e) {
        toast.error('Failed to load your content');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me]);

  // Fetch summary on ref/window change.
  const loadSummary = useCallback(async () => {
    if (!refId) return;
    setLoading(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/improve/${refId}?window=${timeWindow}`);
      if (r.ok) setSummary(await r.json());
      else setSummary(null);
    } finally { setLoading(false); }
  }, [refId, timeWindow]);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  // Fetch the comments thread.
  const loadThread = useCallback(async () => {
    if (!refId) return;
    const qs = new URLSearchParams({ sort });
    if (filterEpisode) qs.set('episode_id', filterEpisode);
    const r = await authFetch(`${BACKEND_URL}/api/improve/${refId}/comments?${qs}`);
    if (r.ok) setThread(await r.json());
  }, [refId, sort, filterEpisode]);

  useEffect(() => { loadThread(); }, [loadThread]);

  // Fetch the chart timeseries for whichever metric is active.
  const loadTimeseries = useCallback(async () => {
    if (!refId) return;
    const r = await authFetch(
      `${BACKEND_URL}/api/improve/${refId}/timeseries?window=${timeWindow}&metric=${overviewMetric}`
    );
    if (r.ok) setTimeseries(await r.json());
  }, [refId, timeWindow, overviewMetric]);

  useEffect(() => { loadTimeseries(); }, [loadTimeseries]);

  const onLike = async (c) => {
    // Optimistic toggle then sync.
    const target = c.comment_id;
    const wasLiked = c.liked_by_me;
    setThread((prev) => {
      const updateOne = (x) => x.comment_id === target
        ? { ...x, liked_by_me: !wasLiked, like_count: x.like_count + (wasLiked ? -1 : 1) }
        : x;
      return {
        ...prev,
        thread: prev.thread.map((p) => ({
          ...updateOne(p),
          replies: (p.replies || []).map(updateOne),
        })),
      };
    });
    try {
      await authFetch(`${BACKEND_URL}/api/comments/${target}/like`, { method: 'POST' });
    } catch {
      loadThread();  // rollback by refetch
    }
  };

  const onReply = (c) => setReplyingTo((prev) => (prev === c.comment_id ? null : c.comment_id));

  const submitReply = async (parent, body) => {
    setBusyReplyId(parent.comment_id);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/items/${encodeURIComponent(parent.ref_id)}/comments`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body, parent_comment_id: parent.comment_id }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || 'Reply failed');
      }
      setReplyingTo(null);
      loadThread();
      toast.success('Reply posted');
    } catch (e) {
      toast.error(e.message || 'Reply failed');
    } finally { setBusyReplyId(null); }
  };

  const activeItem = useMemo(() => items.find((i) => i.ref_id === refId), [items, refId]);
  const s = summary?.summary || {};

  return (
    <main className="max-w-3xl mx-auto px-4 py-6 pb-32" data-testid="improve-page">
      <div className="flex items-center gap-2 mb-5">
        <button onClick={() => navigate(-1)} className="p-2 rounded-lg hover:bg-slate-800" data-testid="improve-back">
          <ArrowLeft className="w-4 h-4 text-slate-300" />
        </button>
        <h1 className="text-2xl sm:text-3xl font-bold text-white">Improve</h1>
      </div>

      {/* Content picker */}
      <div className="relative mb-5">
        {activeItem?.cover_image && (
          <img
            src={resolveCoverUrl(activeItem.cover_image, BACKEND_URL)}
            alt=""
            className="absolute left-3 top-1/2 -translate-y-1/2 w-12 h-12 rounded-md object-cover pointer-events-none border border-slate-700"
          />
        )}
        <select
          value={refId}
          onChange={(e) => { setRefId(e.target.value); setParams({ ref_id: e.target.value }, { replace: true }); }}
          className={`appearance-none w-full ${activeItem?.cover_image ? 'pl-20' : 'pl-4'} pr-12 py-4 rounded-2xl bg-slate-900/70 border border-slate-700 text-white font-bold text-lg focus:outline-none focus:border-indigo-500`}
          data-testid="improve-content-picker"
        >
          {items.length === 0 && <option value="">No content yet — upload something first</option>}
          {items.map((i) => (
            <option key={i.ref_id} value={i.ref_id}>
              {i.title} {i.type === 'series' ? '· series' : `· ${i.subtype || 'ebook'}`}
            </option>
          ))}
        </select>
        <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
      </div>

      {/* Window pills */}
      <div className="flex items-center gap-2 mb-5" data-testid="improve-window-pills">
        <span className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mr-2">Summary</span>
        {WINDOWS.map((w) => (
          <button
            key={w.id}
            type="button"
            onClick={() => setTimeWindow(w.id)}
            className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition ${
              timeWindow === w.id
                ? 'bg-white text-slate-900 border-white'
                : 'bg-transparent text-slate-300 border-slate-600 hover:border-slate-400'
            }`}
            data-testid={`improve-window-${w.id}`}
          >
            {w.label}
          </button>
        ))}
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        <Tile
          icon={Users}
          label="Active Listeners"
          value={fmtCount(s.active_listeners)}
          accent="emerald"
          tooltip="Started a book/audio AND listened ≥1 hour OR read ≥10 000 words"
          testId="improve-tile-listeners"
        />
        <Tile
          icon={MessageCircle}
          label="Comments"
          value={fmtCount(s.comments)}
          accent="indigo"
          tooltip="All comments on this title + its episodes in the selected window"
          testId="improve-tile-comments"
        />
        <Tile
          icon={Sparkles}
          label="Total Revenue Share"
          value="NA"
          accent="amber"
          hint="80% paid out on ebooks · 100 coins = £1.50"
          tooltip="Live subscription split per-title is rolling out — your share is still being computed"
          testId="improve-tile-revenue"
        />
        <Tile
          icon={Clock}
          label="Avg Play Time"
          value={fmtDuration(s.avg_play_time_seconds)}
          accent="cyan"
          tooltip="Mean listening time across qualifying users in the window"
          testId="improve-tile-avg"
        />
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-800 flex gap-6 mb-4">
        {[
          { id: 'analytics', label: 'Analytics' },
          { id: 'comments', label: `Comments${threadData.total ? ` · ${threadData.total}` : ''}` },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`relative pb-2.5 text-sm font-semibold transition ${
              tab === t.id ? 'text-white' : 'text-slate-500 hover:text-slate-300'
            }`}
            data-testid={`improve-tab-${t.id}`}
          >
            {t.label}
            {tab === t.id && (
              <span className="absolute left-0 right-0 -bottom-px h-0.5 rounded-full bg-gradient-to-r from-fuchsia-500 via-purple-500 to-indigo-400" />
            )}
          </button>
        ))}
      </div>

      {loading && (
        <div className="text-center text-slate-400 text-sm py-6">
          <Loader2 className="w-4 h-4 inline-block animate-spin mr-2" /> Loading…
        </div>
      )}

      {tab === 'analytics' && !loading && (
        <section className="space-y-4" data-testid="improve-analytics-pane">
          {/* Sub-tabs: Overview vs Engagement */}
          <div className="flex gap-1 bg-slate-900/60 border border-slate-800 rounded-full p-1 w-fit">
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'engagement', label: 'Engagement' },
            ].map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setAnalyticsView(t.id)}
                className={`px-4 py-1.5 rounded-full text-xs font-bold transition ${
                  analyticsView === t.id
                    ? 'bg-gradient-to-r from-fuchsia-500 to-indigo-500 text-white shadow'
                    : 'text-slate-400 hover:text-white'
                }`}
                data-testid={`improve-analytics-${t.id}`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {analyticsView === 'overview' && (
            <>
              {/* Metric toggle — drives /timeseries?metric= and the chart legend. */}
              <div
                className="flex items-center gap-1.5 flex-wrap"
                data-testid="improve-metric-pills"
              >
                {METRICS.map((m) => {
                  const active = overviewMetric === m.id;
                  return (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => setOverviewMetric(m.id)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition ${
                        active
                          ? 'bg-white text-slate-900 border-white'
                          : 'bg-transparent text-slate-300 border-slate-700 hover:border-slate-400'
                      }`}
                      style={active ? { color: m.stroke, background: '#0f172a', borderColor: m.stroke } : undefined}
                      data-testid={`improve-metric-${m.id}`}
                    >
                      <m.Icon className="w-3.5 h-3.5" />
                      {m.label}
                    </button>
                  );
                })}
              </div>
              <MetricChart
                data={timeseries.series}
                window={timeWindow}
                metric={findMetric(overviewMetric)}
              />
            </>
          )}

          {analyticsView === 'engagement' && (
            <EngagementPane
              episodeCount={timeseries.episode_count}
              unlockAt={timeseries.engagement_unlock_at || 50}
            />
          )}

          <div className="rounded-2xl bg-slate-900/40 border border-slate-800 p-4">
            <p className="text-[11px] uppercase tracking-widest text-slate-400 font-bold mb-2 flex items-center gap-1.5">
              <BarChart3 className="w-3.5 h-3.5" /> How we count
            </p>
            <ul className="space-y-1.5 text-sm text-slate-200">
              <li>
                <span className="font-semibold text-emerald-300">Active listener:</span>{' '}
                a unique user who started this title and either listened for{' '}
                <span className="tabular-nums font-bold">≥ 1 hour</span> or read{' '}
                <span className="tabular-nums font-bold">≥ 10 000 words</span>.
              </li>
              <li>
                <span className="font-semibold text-cyan-300">Average play time:</span>{' '}
                mean total listening time across those qualifying users in the
                window — pulled directly from the listening ledger.
              </li>
              <li>
                <span className="font-semibold text-amber-300">Revenue share:</span>{' '}
                <span className="text-slate-400">NA today.</span> Once subscription
                payouts split per-title goes live, this tile will show your
                cut (80% of ebook revenue · 100 coins ≈ £1.50).
              </li>
            </ul>
          </div>
          {summary?.ref?.type === 'series' && threadData.episodes?.length > 0 && (
            <div className="rounded-2xl bg-slate-900/40 border border-slate-800 p-4">
              <p className="text-[11px] uppercase tracking-widest text-slate-400 font-bold mb-2">
                Episodes in this series
              </p>
              <ul className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                {threadData.episodes
                  .slice()
                  .sort((a, b) => (a.number || 0) - (b.number || 0))
                  .map((ep) => (
                    <li
                      key={ep.ref_id}
                      className="rounded-lg bg-slate-800/60 border border-slate-700 px-2.5 py-1.5 text-slate-200 truncate"
                      title={ep.title}
                    >
                      Ep {ep.number ?? '—'} · {ep.title || 'Untitled'}
                    </li>
                  ))}
              </ul>
            </div>
          )}
        </section>
      )}

      {tab === 'comments' && !loading && (
        <section data-testid="improve-comments-pane">
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {summary?.ref?.type === 'series' && (
              <select
                value={filterEpisode}
                onChange={(e) => setFilterEpisode(e.target.value)}
                className="bg-slate-900 border border-slate-700 text-slate-200 text-xs rounded-full px-3 py-1.5 focus:outline-none focus:border-indigo-500"
                data-testid="improve-episode-filter"
              >
                <option value="">All Episodes</option>
                {threadData.episodes
                  .slice()
                  .sort((a, b) => (a.number || 0) - (b.number || 0))
                  .map((ep) => (
                    <option key={ep.ref_id} value={ep.ref_id}>
                      Ep {ep.number ?? '—'} · {ep.title}
                    </option>
                  ))}
              </select>
            )}
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="bg-slate-900 border border-slate-700 text-slate-200 text-xs rounded-full px-3 py-1.5 focus:outline-none focus:border-indigo-500"
              data-testid="improve-sort"
            >
              <option value="relevant">Most Relevant</option>
              <option value="newest">Newest</option>
            </select>
            <span className="text-[11px] text-slate-500 ml-auto">
              {threadData.total} {threadData.total === 1 ? 'comment' : 'comments'}
            </span>
          </div>

          {threadData.thread.length === 0 ? (
            <p className="text-sm text-slate-500 italic py-6 text-center">
              No comments yet on this {summary?.ref?.type === 'series' ? 'series' : 'ebook'}.
            </p>
          ) : (
            <div className="divide-y divide-slate-800" data-testid="improve-comments-list">
              {threadData.thread.map((c) => (
                <CommentRow
                  key={c.comment_id}
                  c={c}
                  onLike={onLike}
                  onReply={onReply}
                  replyingTo={replyingTo}
                  onSubmitReply={submitReply}
                  busyReplyId={busyReplyId}
                />
              ))}
            </div>
          )}
        </section>
      )}
    </main>
  );
};

export default Improve;
